'use client';

import { useState, useTransition } from 'react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@ps/ui/atoms/dropdown-menu';
import { cn } from '@ps/ui/utils';
import {
  Ban,
  Bell,
  BellOff,
  Bookmark,
  BookmarkX,
  Copy,
  Flag,
  MoreVertical,
  Pin,
  PinOff,
  Trash2,
} from 'lucide-react';

import { useCommunityMessageContext } from '../../contexts/community-message/useCommunityMessageContext';
import { useCommunityContext } from '../../contexts/community/useCommunityContext';
import { formatChannelAuthorName } from '../../functions/utils/formatChannelAuthorName';
import { useDeleteChannelMessage } from '../../hooks/useDeleteChannelMessage';
import { useReportChannelMessage } from '../../hooks/useReportChannelMessage';
import { useToggleBookmarkMessage } from '../../hooks/useToggleBookmarkMessage';
import { useToggleMemberBlock } from '../../hooks/useToggleMemberBlock';
import { useToggleMessageNotification } from '../../hooks/useToggleMessageNotification';
import { useTogglePinMessage } from '../../hooks/useTogglePinMessage';
import type { ExtendedCommunityChannelMessage } from '../../types/ExtendedCommunityChannelMessage';
import { BlockMemberDialog } from './BlockMemberDialog';
import { DeleteChannelMessageDialog } from './DeleteChannelMessageDialog';

interface Props {
  message: ExtendedCommunityChannelMessage;
  currentUserId: string | null | undefined;
  /**
   * Whether the current user is the channel admin. Pin / Delete-any are
   * admin-only. Defaults to the context's `canManage` — `true` on the creator
   * dashboard, `false` on the visitor surface (including an owner previewing
   * their own bio, who deletes only their OWN messages there, like any member).
   */
  isAdmin?: boolean;
  /**
   * Whether the current user has bookmarked this message — drives the
   * Bookmark / Un-bookmark label. Lifted to the bubble so the header badge and
   * this menu share one channel-bookmarks subscription.
   */
  isBookmarked: boolean;
  /**
   * Whether the current user has subscribed to this message's reply-thread
   * notifications — drives the Notifications / Mute Notifications label. Lifted
   * to the bubble so the header bell and this menu share one membership read.
   */
  isSubscribed: boolean;
}

/**
 * Three-dot menu on a channel message — Notifications (subscribe to replies),
 * Pin/Un-pin (admin), Bookmark/Un-bookmark, Copy Reference, Report (other
 * authors), Block/Unblock User (owner moderation of a non-creator author),
 * Delete (own or admin).
 */
export function ChannelMessageMenu({
  message,
  currentUserId,
  isAdmin,
  isBookmarked,
  isSubscribed,
}: Props) {
  const { canManage, creatorId } = useCommunityContext();
  const { pinnedMessagesInvalidated$ } = useCommunityMessageContext();
  const admin = isAdmin ?? canManage;
  const authorName = formatChannelAuthorName(message.profile);
  const isOwn = !!currentUserId && message.userId === currentUserId;
  const canPin = admin && !message.deleted;
  const canDelete = (isOwn || admin) && !message.deleted;
  const canReport = !isOwn && !message.deleted;
  // Owner moderation: block any author that isn't the creator themselves
  // (mirrors the author user card's `canModerate`).
  const canBlock = admin && creatorId != null && message.userId !== creatorId;
  const isPinned = message.pinnedAt != null;

  const togglePin = useTogglePinMessage({
    channelId: message.channelId,
    messageId: message.id,
  });
  const toggleBookmark = useToggleBookmarkMessage({
    channelId: message.channelId,
    messageId: message.id,
  });
  const toggleNotification = useToggleMessageNotification({
    channelId: message.channelId,
    messageId: message.id,
  });
  const deleteMessage = useDeleteChannelMessage({
    channelId: message.channelId,
    messageId: message.id,
  });
  const reportMessage = useReportChannelMessage({
    channelId: message.channelId,
    messageId: message.id,
  });
  const toggleBlock = useToggleMemberBlock({
    channelId: message.channelId,
    memberId: message.userId,
  });
  const [pending, startTransition] = useTransition();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [confirmingBlock, setConfirmingBlock] = useState(false);
  // A visible message means the author isn't blocked (blocked authors'
  // messages are filtered out); flips on toggle so a block+unblock within the
  // session reads correctly. Mirrors the author user card's local state.
  const [blocked, setBlocked] = useState(false);

  const run = (fn: () => Promise<void>) => {
    if (pending) return;
    startTransition(async () => {
      try {
        await fn();
      } catch (error) {
        console.error('community channel message action failed', error);
      }
    });
  };

  const onCopyReference = async () => {
    const ref = `${message.channelId}#${message.id}`;
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(ref);
      } catch {
        // Clipboard write can fail when the page isn't in a secure
        // context or the user denied permission. Silent — the menu
        // still closes and the user can copy manually if needed.
      }
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Message actions"
            className={cn(
              'hover:text-content-1 text-content-3',
              'data-[state=open]:text-content-1',
            )}
          >
            <MoreVertical className="h-4 w-4" aria-hidden />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="text-content-1 border-line bg-surface-1"
        >
          {!message.deleted ? (
            <DropdownMenuItem
              onClick={() => run(() => toggleNotification())}
              className={
                isSubscribed ? 'text-red-400 focus:text-red-300' : undefined
              }
            >
              {isSubscribed ? (
                <>
                  <BellOff className="h-4 w-4" aria-hidden /> Mute Notifications
                </>
              ) : (
                <>
                  <Bell className="h-4 w-4" aria-hidden /> Notifications
                </>
              )}
            </DropdownMenuItem>
          ) : null}
          {canPin ? (
            <DropdownMenuItem
              onClick={() =>
                run(async () => {
                  await togglePin({ currentlyPinned: isPinned });
                  // Refresh the pinned banner/pane off the user's own action
                  // directly (the per-message listener also fires this, but
                  // this path doesn't depend on that listener being live).
                  pinnedMessagesInvalidated$.next();
                })
              }
            >
              {isPinned ? (
                <>
                  <PinOff className="h-4 w-4" aria-hidden /> Un-pin from Channel
                </>
              ) : (
                <>
                  <Pin className="h-4 w-4" aria-hidden /> Pin to Channel
                </>
              )}
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuItem
            onClick={() =>
              run(() => toggleBookmark({ currentlyBookmarked: isBookmarked }))
            }
          >
            {isBookmarked ? (
              <>
                <BookmarkX className="h-4 w-4" aria-hidden /> Un-bookmark
              </>
            ) : (
              <>
                <Bookmark className="h-4 w-4" aria-hidden /> Bookmark
              </>
            )}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onCopyReference}>
            <Copy className="h-4 w-4" aria-hidden /> Copy Reference
          </DropdownMenuItem>
          {canReport ? (
            <DropdownMenuItem onClick={() => run(() => reportMessage())}>
              <Flag className="h-4 w-4" aria-hidden /> Report
            </DropdownMenuItem>
          ) : null}
          {canBlock || canDelete ? (
            <DropdownMenuSeparator className="bg-surface-2" />
          ) : null}
          {canBlock ? (
            <DropdownMenuItem
              onSelect={() => setConfirmingBlock(true)}
              className="text-red-400 focus:text-red-300"
            >
              <Ban className="h-4 w-4" aria-hidden />{' '}
              {blocked ? 'Unblock User' : 'Block User'}
            </DropdownMenuItem>
          ) : null}
          {canDelete ? (
            <DropdownMenuItem
              onSelect={() => setConfirmingDelete(true)}
              className="text-red-400 focus:text-red-300"
            >
              <Trash2 className="h-4 w-4" aria-hidden /> Delete
            </DropdownMenuItem>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
      <DeleteChannelMessageDialog
        open={confirmingDelete}
        onOpenChange={setConfirmingDelete}
        onConfirm={async () => {
          await deleteMessage();
          // Soft-delete leaves `pinnedAt` intact, so the per-message
          // listener won't flag a pin change — nudge the pinned views
          // to drop the now-deleted message themselves.
          if (isPinned) pinnedMessagesInvalidated$.next();
        }}
      />
      <BlockMemberDialog
        open={confirmingBlock}
        onOpenChange={setConfirmingBlock}
        blocked={blocked}
        memberName={authorName}
        onConfirm={async () => {
          await toggleBlock();
          setBlocked(value => !value);
        }}
      />
    </>
  );
}
